import React, { useState, useEffect } from 'react';
import { ImageUploader } from '../../ui/image-uploader';
import { Id } from '../../../convex/_generated/dataModel';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../ui/card';
import { useAuth } from '../../../lib/auth/AuthContext';

// Define proper type that doesn't rely on schema recognition
type FileId = string;

interface BackgroundImageUploaderProps {
  projectId: Id<"projects">;
  componentId?: Id<"pageComponents">;
  onImageChange?: (fileId: FileId | null, imageUrl: string) => void;
  // Allow passing an existing image directly
  existingImage?: string;
  existingImageId?: FileId;
}

export const BackgroundImageUploader = ({
  projectId,
  componentId,
  onImageChange,
  existingImage,
  existingImageId
}: BackgroundImageUploaderProps) => {
  const { useAuthenticatedQuery, useAuthenticatedMutation } = useAuth();
  const [fileId, setFileId] = useState<FileId | null>(existingImageId || null);
  const [imageUrl, setImageUrl] = useState<string | null>(existingImage || null);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  console.log('BackgroundImageUploader initialized with:', { 
    projectId: projectId.toString(), 
    componentId: componentId?.toString(),
    existingImage: existingImage ? 'present' : 'absent',
    existingImageId
  });

  // Use authenticated API helpers
  const existingFiles = useAuthenticatedQuery('filesFunctions:listFilesByProjectFn', {
    projectId,
    purpose: "background",
  }) || [];
  
  const generateUploadUrl = useAuthenticatedMutation('filesFunctions:generateUploadUrlFn');
  const saveFileMetadata = useAuthenticatedMutation('filesFunctions:saveFileMetadataFn');
  const removeFile = useAuthenticatedMutation('filesFunctions:removeFileFn');

  // Get files related to this specific component if we have a componentId
  const componentFiles = useAuthenticatedQuery(
    'filesFunctions:listFilesByComponentFn', 
    componentId ? { componentId } : "skip"
  );

  // Handle directly provided existing image and ID props (highest priority)
  useEffect(() => {
    if (existingImageId && existingImage) {
      console.log('Using directly provided existing image:', { 
        existingImageId, 
        existingImage 
      });
      setFileId(existingImageId);
      setImageUrl(existingImage);
      setIsInitialized(true);
    }
  }, [existingImageId, existingImage]);

  // Set initial state if there's an existing background image from the queries
  useEffect(() => {
    // Skip if we already have an image from props
    if (isInitialized || (existingImageId && existingImage)) return;
    
    // First, try to find files associated with this specific component
    if (componentId && componentFiles && componentFiles.length > 0) {
      // Use the most recent background image associated with this component
      const latestImage = componentFiles.reduce((latest, current) => {
        return latest.createdAt > current.createdAt ? latest : current;
      });
      
      // Convert Convex ID to string for storage in our state
      const latestFileId = latestImage._id.toString();
      console.log('Found existing component-specific background image:', { 
        _id: latestFileId, 
        url: latestImage.url,
        filename: latestImage.filename
      });
      
      setFileId(latestFileId);
      setImageUrl(latestImage.url);
      setIsInitialized(true);
      
      // Notify parent component if needed
      if (onImageChange && latestImage.url) {
        onImageChange(latestFileId, latestImage.url);
      }
      return;
    }
    
    // Fallback to project-level background images
    if (existingFiles && existingFiles.length > 0) {
      // Use the most recent background image
      const latestImage = existingFiles.reduce((latest, current) => {
        return latest.createdAt > current.createdAt ? latest : current;
      });
      
      // Convert Convex ID to string for storage in our state
      const latestFileId = latestImage._id.toString();
      console.log('Found existing project-level background image:', { 
        _id: latestFileId, 
        url: latestImage.url,
        filename: latestImage.filename
      });
      
      setFileId(latestFileId);
      setImageUrl(latestImage.url);
      setIsInitialized(true);
      
      // Notify parent component if needed
      if (onImageChange && latestImage.url) {
        onImageChange(latestFileId, latestImage.url);
      }
    } else {
      console.log('No existing background images found');
    }
  }, [existingFiles, componentFiles, componentId, onImageChange, isInitialized, existingImage, existingImageId]);

  // Handle upload completion
  const handleUploadComplete = (newFileId: Id<"files"> | null, newImageUrl: string) => {
    // Store the ID as a string (safer approach)
    const fileIdString = newFileId ? newFileId.toString() : null;
    
    console.log('Background image upload completed:', { 
      newFileId: newFileId?.toString(), 
      newImageUrl,
      fileIdString
    });
    
    setFileId(fileIdString);
    setImageUrl(newImageUrl);
    setIsInitialized(true);
    
    if (onImageChange) {
      console.log('Notifying parent component of image change');
      onImageChange(fileIdString, newImageUrl);
    }
  };

  // Handle upload errors
  const handleUploadError = (error: string) => {
    console.error('Background image upload error:', error);
    // You could add a toast notification here if desired
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Background Image</CardTitle>
        <CardDescription>
          Upload a background image for your project. 
          Recommended size: 1920x1080px. Max size: 2MB.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ImageUploader 
          projectId={projectId}
          componentId={componentId}
          purpose="background"
          altText="Project Background"
          maxSizeMB={10}
          acceptedTypes={['image/jpeg', 'image/png', 'image/webp']}
          existingImageUrl={imageUrl || undefined}
          existingFileId={fileId || undefined}
          onUploadComplete={handleUploadComplete}
          onUploadError={handleUploadError}
          enableCompression={true}
          compressionThresholdKB={200}
          showHelper={true}
        />
        
        {imageUrl && (
          <div className="mt-4 p-2 bg-gray-50 rounded-md border border-gray-200">
            <p className="text-sm font-medium">Current Background:</p>
            <div className="mt-1 h-20 bg-cover bg-center rounded" 
                 style={{ backgroundImage: `url(${imageUrl})` }} />
          </div>
        )}
        
        <div className="mt-4 text-xs text-gray-500">
          <p>Best practices:</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>Use high-quality images for better visual impact</li>
            <li>Choose images with good contrast for text overlay</li>
            <li>Images should be at least 1920x1080px for optimal display</li>
            <li>WebP format is recommended for better performance</li>
            <li>Consider accessibility - images shouldn't make text hard to read</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
}; 